/**
 * Where a job's quality points go: what each broken rule costs, per-family verdict counts
 * and the KPI notes, all read from the latest phase (after when it ran, else before).
 * Kept out of the components so the Overview and the Problems list rank rules the same way.
 */
import type {
  CheckScore,
  ProductScore,
  ScoreProductOrder,
  ScoreVerdict,
} from '../types/quality-scores.types';

import { latestOfRow } from './product-quality';
import { latestFails, PASS_GATE, REVIEW_GATE } from './quality-labels';

/**
 * Points a field loses each time a rule of this severity fails on it — the scorer's own
 * deductions (critical 25 · major 10 · minor 5). Unknown severities cost nothing.
 */
export const SEVERITY_DEDUCTION: Record<string, number> = {
  CRITICAL: 25,
  MAJOR: 10,
  MINOR: 5,
};

/** Products loaded for the Overview's family chart and "closest to Pass" note (one request). */
export const OVERVIEW_PRODUCT_LIMIT = 500;

/** The deduction one failure of this severity costs. */
export function severityDeduction(severity: string): number {
  return SEVERITY_DEDUCTION[severity] ?? 0;
}

/** One rule with its latest-phase failures and the field points they cost. */
export interface RuleImpact {
  check: CheckScore;
  fails: number;
  evaluated: number;
  /** Field points lost = failures in the latest phase × the severity's deduction. */
  lost: number;
}

/**
 * Every rule with its field points lost (fails in the latest phase × deduction), most
 * points first; ties go to the rule failing more products, then the more serious one.
 */
export function rankByPointsLost(checks: CheckScore[], showPost: boolean): RuleImpact[] {
  return checks
    .map((check) => {
      const { fails, evaluated } = latestFails(check, showPost);
      return { check, fails, evaluated, lost: fails * severityDeduction(check.severity) };
    })
    .sort(
      (a, b) =>
        b.lost - a.lost ||
        b.fails - a.fails ||
        severityDeduction(b.check.severity) - severityDeduction(a.check.severity),
    );
}

/** A Pareto row: the rule plus the running share of ALL points lost up to and including it. */
export interface ParetoRow extends RuleImpact {
  /** 0–1: cumulative points lost through this row over the total across every rule. */
  cumulative: number;
}

/**
 * The top `limit` rules by points lost with a running share of the total over every rule,
 * plus the share the shown rules cause together ("these 8 rules cause 78%").
 */
export function paretoRows(
  ranked: RuleImpact[],
  limit: number,
): { rows: ParetoRow[]; total: number; shownShare: number } {
  const total = ranked.reduce((sum, r) => sum + r.lost, 0);
  let running = 0;
  const rows = ranked
    .filter((r) => r.lost > 0)
    .slice(0, limit)
    .map((r) => {
      running += r.lost;
      return { ...r, cumulative: total > 0 ? running / total : 0 };
    });
  return { rows, total, shownShare: total > 0 ? running / total : 0 };
}

/** One product family's latest verdict split and average score. */
export interface FamilyVerdicts {
  vertical: string;
  products: number;
  counts: Record<ScoreVerdict, number>;
  average: number | null;
}

/**
 * Latest-phase verdicts per product family from a loaded product list, biggest family
 * first — the summary's per-family split is after-only, so a before-only job would read 0.
 */
export function familyVerdicts(products: ProductScore[], showPost: boolean): FamilyVerdicts[] {
  const byFamily = new Map<
    string,
    { counts: Record<ScoreVerdict, number>; scores: number[]; n: number }
  >();
  for (const product of products) {
    const entry = byFamily.get(product.vertical) ?? {
      counts: { PASS: 0, REVIEW: 0, FAIL: 0 },
      scores: [],
      n: 0,
    };
    const { score, verdict } = latestOfRow(product, showPost);
    entry.n += 1;
    if (verdict === 'PASS' || verdict === 'REVIEW' || verdict === 'FAIL')
      entry.counts[verdict] += 1;
    if (score !== null) entry.scores.push(score);
    byFamily.set(product.vertical, entry);
  }
  return [...byFamily.entries()]
    .map(([vertical, e]) => ({
      vertical,
      products: e.n,
      counts: e.counts,
      average: e.scores.length > 0 ? e.scores.reduce((a, b) => a + b, 0) / e.scores.length : null,
    }))
    .sort((a, b) => b.products - a.products || b.counts.FAIL - a.counts.FAIL);
}

/**
 * The best score among Needs-review products still under 90, or null when there is none.
 * Only REVIEW counts: a Fail at 85 (critical problem) is not "close to Pass".
 */
export function closestToPass(products: ProductScore[], showPost: boolean): number | null {
  let best: number | null = null;
  for (const product of products) {
    const { score, verdict } = latestOfRow(product, showPost);
    if (verdict !== 'REVIEW' || score === null || score >= PASS_GATE) continue;
    if (best === null || score > best) best = score;
  }
  return best;
}

/**
 * Failing products that still score 70+, out of every failing product in the list. The list
 * has no severities, so such a Fail is either a critical problem or a field under 70.
 */
export function failingAtReviewScore(
  products: ProductScore[],
  showPost: boolean,
): { failing: number; atReviewScore: number } {
  let failing = 0;
  let atReviewScore = 0;
  for (const product of products) {
    const { score, verdict } = latestOfRow(product, showPost);
    if (verdict !== 'FAIL') continue;
    failing += 1;
    if (score !== null && score >= REVIEW_GATE) atReviewScore += 1;
  }
  return { failing, atReviewScore };
}

/** Rules failing at least once in a phase, and how many of them fail every product scored. */
export function brokenRules(
  checks: CheckScore[],
  phase: 'pre' | 'post',
  products: number,
): { broken: number; onEvery: number } {
  let broken = 0;
  let onEvery = 0;
  for (const check of checks) {
    const fails = phase === 'post' ? check.failsPost : check.failsPre;
    if (fails <= 0) continue;
    broken += 1;
    if (products > 0 && fails >= products) onEvery += 1;
  }
  return { broken, onEvery };
}

/** "70–80" for bucket 7; the last bucket includes 100. */
export function bucketLabel(index: number): string {
  return `${String(index * 10)}–${String(index * 10 + 10)}`;
}

/** "1 product" / "12 products". */
export function productWord(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'product' : 'products'}`;
}

/** Ten counts, padded or trimmed so a short histogram never breaks the chart. */
export function tenBuckets(histogram: number[] | null | undefined): number[] {
  return Array.from({ length: 10 }, (_, i) => histogram?.[i] ?? 0);
}

/**
 * The spread in one plain sentence — the lowest and highest band in use and how many
 * products reach 90 — so the chart has a text alternative that says what it shows.
 */
export function spreadSentence(histogram: number[]): string {
  const buckets = tenBuckets(histogram);
  const total = buckets.reduce((sum, n) => sum + n, 0);
  if (total === 0) return 'No product has a score yet.';
  const lowest = buckets.findIndex((n) => n > 0);
  const highest = 9 - [...buckets].reverse().findIndex((n) => n > 0);
  const top = buckets[9] ?? 0;
  const range =
    lowest === highest
      ? `Every product scores ${bucketLabel(lowest)}`
      : `Scores run from ${String(lowest * 10)} to ${String(highest * 10 + 10)}`;
  const pass =
    top === 0
      ? 'none reaches 90'
      : top === total
        ? 'all reach 90 or more'
        : `${top.toLocaleString()} of ${total.toLocaleString()} reach 90 or more`;
  return `${range}; ${pass}.`;
}

/** Score width of one bubble bin: 2.5 points, so bins line up with the 70 and 90 gates. */
export const BIN_SCORE_STEP = 2.5;
/** Problem-count height of one bubble bin: 2 problems (n and n + 1). */
export const BIN_PROBLEM_STEP = 2;

/** Where a bin starts: its lowest score and lowest problem count (the table filter's chip). */
export interface BinRange {
  scoreFrom: number;
  problemsFrom: number;
}

/** One bubble: products with a similar latest score and problem count, with their verdict split. */
export interface ProductBin extends BinRange {
  key: string;
  scoreTo: number;
  problemsTo: number;
  products: ProductScore[];
  counts: Record<ScoreVerdict, number>;
  /** The most common product family in the bin and its share (0–1). */
  topFamily: { vertical: string; share: number } | null;
}

/** "82.5:10" — a bin's identity, stable across renders. */
export function binKey(range: BinRange): string {
  return `${String(range.scoreFrom)}:${String(range.problemsFrom)}`;
}

/**
 * The score axis start: 50, or the ten below the worst score when a product scores under 50,
 * so every bubble fits and the bins still align with 70 and 90.
 */
export function binAxisStart(minScore: number): number {
  return Math.max(0, Math.min(50, Math.floor(minScore / 10) * 10));
}

/** The bin a latest score / problem count falls in, from axis start `xMin`; 100 joins the last bin. */
function binOf(score: number, problems: number, xMin: number): BinRange {
  const last = Math.round((100 - xMin) / BIN_SCORE_STEP) - 1;
  const index = Math.max(0, Math.min(last, Math.floor((score - xMin) / BIN_SCORE_STEP)));
  return {
    scoreFrom: xMin + index * BIN_SCORE_STEP,
    problemsFrom: Math.floor(Math.max(0, problems) / BIN_PROBLEM_STEP) * BIN_PROBLEM_STEP,
  };
}

/**
 * Groups loaded products into score × problem bins (each product's latest phase), with each
 * bin's verdict split and most common family. Products without a latest score are left out.
 */
export function binProducts(
  products: ProductScore[],
  showPost: boolean,
): { bins: ProductBin[]; xMin: number; maxProblems: number } {
  const scored = products
    .map((row) => ({ row, latest: latestOfRow(row, showPost) }))
    .filter(
      (
        p,
      ): p is { row: ProductScore; latest: { score: number; verdict: string; problems: number } } =>
        p.latest.score !== null,
    );
  const xMin = binAxisStart(scored.reduce((m, p) => Math.min(m, p.latest.score), 100));
  const byKey = new Map<string, ProductBin>();
  let maxProblems = 0;
  for (const { row, latest } of scored) {
    const range = binOf(latest.score, latest.problems, xMin);
    const key = binKey(range);
    const bin = byKey.get(key) ?? {
      ...range,
      key,
      scoreTo: range.scoreFrom + BIN_SCORE_STEP,
      problemsTo: range.problemsFrom + BIN_PROBLEM_STEP - 1,
      products: [],
      counts: { PASS: 0, REVIEW: 0, FAIL: 0 },
      topFamily: null,
    };
    bin.products.push(row);
    if (latest.verdict === 'PASS' || latest.verdict === 'REVIEW' || latest.verdict === 'FAIL') {
      bin.counts[latest.verdict] += 1;
    }
    maxProblems = Math.max(maxProblems, latest.problems);
    byKey.set(key, bin);
  }
  const bins = [...byKey.values()].map((bin) => {
    const families = new Map<string, number>();
    for (const row of bin.products)
      families.set(row.vertical, (families.get(row.vertical) ?? 0) + 1);
    const [vertical, n] = [...families.entries()].sort((a, b) => b[1] - a[1])[0] ?? ['', 0];
    return {
      ...bin,
      topFamily: n > 0 ? { vertical, share: n / bin.products.length } : null,
    };
  });
  return { bins, xMin, maxProblems };
}

/** Whether a product's latest score and problems fall in the bin starting at `range`. */
export function inBin(row: ProductScore, showPost: boolean, range: BinRange): boolean {
  const { score, problems } = latestOfRow(row, showPost);
  if (score === null) return false;
  const top = range.scoreFrom + BIN_SCORE_STEP;
  const inScore = score >= range.scoreFrom && (score < top || (top >= 100 && score <= 100));
  return (
    inScore && problems >= range.problemsFrom && problems < range.problemsFrom + BIN_PROBLEM_STEP
  );
}

/** "Score 82.5–85 · 10–11 problems" — a bin's range in words, for its chip and hover card. */
export function binLabel(range: BinRange): string {
  const top = range.scoreFrom + BIN_SCORE_STEP;
  const p = range.problemsFrom;
  return `Score ${range.scoreFrom.toLocaleString()}–${top.toLocaleString()} · ${String(p)}–${String(p + BIN_PROBLEM_STEP - 1)} problems`;
}

/** Nulls last, whatever the direction. */
function byNullable(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return (a - b) * dir;
}

/**
 * Sorts a client-side list the way the service orders `/scores/products`: most improved, worst
 * after, worst before, or by product id — so a bubble's rows follow the Sort select too.
 */
export function sortProducts(rows: ProductScore[], order: ScoreProductOrder): ProductScore[] {
  const copy = [...rows];
  if (order === 'delta') return copy.sort((a, b) => byNullable(a.delta, b.delta, -1));
  if (order === 'post') return copy.sort((a, b) => byNullable(a.post, b.post, 1));
  if (order === 'pre') return copy.sort((a, b) => byNullable(a.pre, b.pre, 1));
  return copy.sort((a, b) => a.productId.localeCompare(b.productId));
}
